import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OBJETIVOS_PUBLICOS = [
  { id: "ganho_muscular", label: "Ganho Muscular" },
  { id: "perda_peso", label: "Perda de Peso" },
  { id: "perda_gordura", label: "Perda de Gordura" },
  { id: "tonificacao", label: "Tonificação" },
  { id: "resistencia", label: "Resistência" },
  { id: "mobilidade", label: "Mobilidade" },
  { id: "pre_parto", label: "Pré-Parto" },
  { id: "pos_parto", label: "Pós-Parto" },
  { id: "jovens_atletas", label: "Jovens Atletas" },
  { id: "senior_60", label: "Sénior +60" },
  { id: "outro", label: "Outro / Não tenho a certeza" },
];

export default function LeadForm() {
  const [form, setForm] = useState({
    nome: "",
    email: "",
    telefone: "",
    objetivo: "",
    mensagem: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome.trim()) {
      setError("Indica o teu nome.");
      return;
    }
    if (!form.email.trim() && !form.telefone.trim()) {
      setError("Indica pelo menos um email ou telemóvel.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        nome: form.nome.trim(),
        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        objetivo: form.objetivo || null,
        mensagem: form.mensagem.trim() || null,
      });
      setSuccess(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Não foi possível enviar. Tenta novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      className="px-6 md:px-12 lg:px-24 py-16 md:py-24 bg-[#0a0a0a]"
      data-testid="lead-form-section"
    >
      <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-10 md:gap-16 items-start">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10 bg-yellow-400" />
            <span className="overline">Pede a tua sessão experimental</span>
          </div>
          <h2 className="font-display uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">
            Pronto para <span className="text-yellow-400">começar</span>?
          </h2>
          <p className="mt-5 text-neutral-300 text-base leading-relaxed max-w-md">
            Deixa os teus dados e o Pedro entra em contacto contigo nas próximas
            24h para combinar a tua <strong className="text-white">sessão experimental gratuita</strong>
            {" "}e perceber juntos qual é o melhor plano para ti.
          </p>
          <ul className="mt-6 space-y-2 text-sm text-neutral-400">
            <li className="flex items-start gap-2">
              <span className="text-yellow-400 mt-0.5">▸</span>
              Atendimento exclusivo, sem multidão
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-400 mt-0.5">▸</span>
              Treino adaptado às tuas limitações e objetivos
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-400 mt-0.5">▸</span>
              Acompanhamento contínuo e medições corporais regulares
            </li>
          </ul>
        </div>

        {success ? (
          <div
            className="card p-8 border-yellow-400/40 bg-yellow-400/5 text-center"
            data-testid="lead-form-success"
          >
            <CheckCircle2 className="text-yellow-400 mx-auto mb-3" size={40} />
            <h3 className="font-display text-2xl uppercase tracking-tight mb-2">
              Obrigado!
            </h3>
            <p className="text-neutral-300 text-sm">
              Recebemos a tua mensagem. O Pedro vai entrar em contacto contigo
              em breve.
            </p>
            <button
              type="button"
              onClick={() => {
                setSuccess(false);
                setForm({ nome: "", email: "", telefone: "", objetivo: "", mensagem: "" });
              }}
              className="btn-ghost mt-6 text-xs"
              data-testid="lead-form-new"
            >
              Enviar nova mensagem
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="card p-6 md:p-7 space-y-4"
            data-testid="lead-form"
          >
            <div>
              <label className="overline block mb-2">Nome *</label>
              <input
                type="text"
                className="input w-full"
                placeholder="O teu nome"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                data-testid="lead-form-nome"
                required
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-2">Email</label>
                <input
                  type="email"
                  className="input w-full"
                  placeholder="exemplo@email.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="lead-form-email"
                />
              </div>
              <div>
                <label className="overline block mb-2">Telemóvel</label>
                <input
                  type="tel"
                  className="input w-full"
                  placeholder="9XX XXX XXX"
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                  data-testid="lead-form-telefone"
                />
              </div>
            </div>
            <div>
              <label className="overline block mb-2">Objetivo principal</label>
              <select
                className="input w-full"
                value={form.objetivo}
                onChange={(e) => setForm({ ...form, objetivo: e.target.value })}
                data-testid="lead-form-objetivo"
              >
                <option value="">— Seleciona um objetivo (opcional) —</option>
                {OBJETIVOS_PUBLICOS.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="overline block mb-2">Mensagem</label>
              <textarea
                rows={3}
                className="input w-full resize-none"
                placeholder="Conta-nos um pouco sobre ti (opcional)"
                value={form.mensagem}
                onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                data-testid="lead-form-mensagem"
              />
            </div>

            {error ? (
              <div
                className="text-sm text-yellow-300 bg-yellow-400/10 border border-yellow-400/30 px-3 py-2 rounded"
                data-testid="lead-form-error"
              >
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full inline-flex items-center justify-center gap-2"
              data-testid="lead-form-submit"
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Send size={16} />
              )}
              {loading ? "A enviar..." : "Pedir sessão experimental"}
            </button>
            <p className="text-[11px] text-neutral-500 leading-relaxed">
              Os teus dados são confidenciais e usados apenas para te contactarmos
              sobre a tua sessão experimental.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
